import { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { MapContainer, TileLayer, Marker, Popup } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { LayoutGrid, Map as MapIcon, SlidersHorizontal, Users } from "lucide-react";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import EventCard from "@/components/EventCard";
import CommunityCard from "@/components/CommunityCard";
import { api, CATEGORIES, FORMATS, CITIES } from "@/lib/api";
import { useLang } from "@/lib/i18n";

const markerIcon = new L.Icon({
  iconUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png",
  iconRetinaUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png",
  shadowUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png",
  iconSize: [25, 41],
  iconAnchor: [12, 41],
});

const ALL = "__all__";

export default function ExplorePage() {
  const [params, setSearchParams] = useSearchParams();
  const { t } = useLang();
  const [events, setEvents] = useState([]);
  const [communities, setCommunities] = useState([]);
  const [category, setCategory] = useState(ALL);
  const [format, setFormat] = useState(ALL);
  const cityParam = params.get("city") || ALL;
  const searchParam = params.get("search") || "";
  const [city, setCity] = useState(cityParam);
  const [search, setSearch] = useState(searchParam);
  const [debouncedSearch, setDebouncedSearch] = useState(searchParam);

  useEffect(() => { setCity(cityParam); }, [cityParam]);
  useEffect(() => { setSearch(searchParam); }, [searchParam]);
  useEffect(() => {
    const id = setTimeout(() => setDebouncedSearch(search), 350);
    return () => clearTimeout(id);
  }, [search]);

  const changeCity = (v) => {
    setCity(v);
    const p = new URLSearchParams(params);
    if (v === ALL) p.delete("city"); else p.set("city", v);
    setSearchParams(p);
  };

  const load = useCallback(() => {
    const q = {};
    if (category !== ALL) q.category = category;
    if (format !== ALL) q.format = format;
    if (city !== ALL) q.city = city;
    if (debouncedSearch.trim()) q.search = debouncedSearch.trim();
    api.get("/events", { params: q }).then((r) => setEvents(r.data)).catch(() => {});
  }, [category, format, city, debouncedSearch]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    api.get("/communities", { params: city !== ALL ? { city } : {} })
      .then((r) => setCommunities(r.data)).catch(() => {});
  }, [city]);

  const center = useMemo(() => (events.length ? [events[0].lat, events[0].lng] : [-6.2, 106.85]), [events]);

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 py-12">
      <h1 className="font-heading font-black text-3xl md:text-4xl text-slate-900 tracking-tight">{t("explore_title")}</h1>
      <p className="text-slate-500 mt-2">{t("explore_sub")}</p>

      <div data-testid="filter-toolbar" className="bg-white rounded-2xl border border-slate-200 shadow-[0_8px_32px_rgba(0,0,0,0.04)] p-4 mt-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3 items-center">
        <div className="flex items-center gap-2 text-sm font-bold text-slate-600">
          <SlidersHorizontal className="w-4 h-4" /> Filter:
        </div>
        <Select value={category} onValueChange={setCategory}>
          <SelectTrigger data-testid="filter-category" className="rounded-xl"><SelectValue placeholder="Kategori" /></SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL}>Semua Kategori</SelectItem>
            {CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={format} onValueChange={setFormat}>
          <SelectTrigger data-testid="filter-format" className="rounded-xl"><SelectValue placeholder="Format" /></SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL}>Semua Format</SelectItem>
            {FORMATS.map((f) => <SelectItem key={f} value={f}>{f}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={city} onValueChange={changeCity}>
          <SelectTrigger data-testid="filter-city" className="rounded-xl"><SelectValue placeholder="Kota" /></SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL}>Semua Kota</SelectItem>
            {CITIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
          </SelectContent>
        </Select>
        <Input data-testid="explore-search-input" placeholder="Cari aksi..." value={search} onChange={(e) => setSearch(e.target.value)} className="rounded-xl" />
      </div>

      <Tabs defaultValue="grid" className="mt-8">
        <TabsList className="rounded-full bg-slate-100 p-1">
          <TabsTrigger data-testid="view-grid-tab" value="grid" className="rounded-full font-bold data-[state=active]:bg-white">
            <LayoutGrid className="w-4 h-4 mr-1.5" /> Grid
          </TabsTrigger>
          <TabsTrigger data-testid="view-map-tab" value="map" className="rounded-full font-bold data-[state=active]:bg-white">
            <MapIcon className="w-4 h-4 mr-1.5" /> Peta
          </TabsTrigger>
        </TabsList>

        <TabsContent value="grid" className="mt-6">
          {events.length === 0 ? (
            <p data-testid="no-events-message" className="text-center text-slate-400 py-16 font-semibold">Tidak ada aksi yang cocok dengan filter ini.</p>
          ) : (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {events.map((e) => <EventCard key={e.id} event={e} onRegistered={load} />)}
            </div>
          )}
        </TabsContent>

        <TabsContent value="map" className="mt-6">
          <div data-testid="map-container" className="h-[520px] rounded-2xl overflow-hidden border border-slate-200">
            <MapContainer center={center} zoom={7} scrollWheelZoom>
              <TileLayer
                attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              />
              {events.map((e) => (
                <Marker key={e.id} position={[e.lat, e.lng]} icon={markerIcon}>
                  <Popup>
                    <div className="space-y-1">
                      <p className="font-bold">{e.title}</p>
                      <p className="text-xs">{e.address}, {e.city}</p>
                      <p className="text-xs">{e.date} · {e.registered}/{e.capacity} relawan</p>
                    </div>
                  </Popup>
                </Marker>
              ))}
            </MapContainer>
          </div>
        </TabsContent>
      </Tabs>

      <div className="mt-16">
        <div className="flex items-center gap-2 mb-6">
          <Users className="w-6 h-6 text-emerald-600" />
          <h2 className="font-heading font-black text-2xl text-slate-900 tracking-tight">Komunitas Terverifikasi</h2>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {communities.length === 0 ? (
            <p data-testid="no-communities-message" className="text-slate-400 font-semibold py-8 col-span-full">Tidak ada komunitas di kota ini.</p>
          ) : (
            communities.map((c) => <CommunityCard key={c.id} community={c} />)
          )}
        </div>
      </div>
    </div>
  );
}
